import * as go from 'gojs';

import { NODE_SIZE } from 'src/containers/ProjectDiagram/constants/styles';
import {
  ArtificialLink,
  D3LayoutLink,
  D3LayoutModel,
  D3LayoutNode,
} from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/types';
import { getTextLength } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/getTextLength';
import { getWidthOfAllChildren } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/getWidthOfAllChildren';
import { toLayoutKey } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/keys';
import { ForceLayoutSettings } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';

const ARTIFICIAL_LINK_PREFIX = '++ARTIFICIAL_LINK++';
const EDGE_PROPERTY_LEFT_LINK_PREFIX = '++EDGE_PROPERTY_LEFT++';
const EDGE_PROPERTY_RIGHT_LINK_PREFIX = '++EDGE_PROPERTY_RIGHT++';

export const mapToLayoutModel = (
  layoutSettings: ForceLayoutSettings,
  nodes: go.Set<go.Node>,
  links: go.Set<go.Link>,
  artificialLinks: ArtificialLink[],
  getLinkLabelForLink: (link: go.Link) => go.TextBlock | null
): D3LayoutModel => {
  const layoutNodes = mapNodesToLayoutNodes(nodes, links);
  const nodeKeys = new Set(layoutNodes.map((node) => node.key));
  return {
    nodes: layoutNodes,
    links: [
      ...mapLinksToLayoutLinks(layoutSettings, links, getLinkLabelForLink),
      ...mapArtificialLinksToLayoutLinks(artificialLinks),
    ].filter(
      (link) =>
        nodeKeys.has(link.source as string) &&
        nodeKeys.has(link.target as string)
    ),
  };
};

const mapCommonNodeToLayoutNode = (node: go.Node): D3LayoutNode => {
  const result: D3LayoutNode = {
    key: toLayoutKey(node.diagram?.model?.getKeyForNodeData(node.data)),
    radius: NODE_SIZE / 2,
    x: node.position.x,
    y: node.position.y,
    isLabel: false,
  };
  if (node.position.isReal() && !(node as ExtendedNode).forceNewPosition) {
    result.fx = node.position.x;
    result.fy = node.position.y;
  }
  return result;
};

const mapLinkLabelToLayoutNode = (
  node: go.Node,
  links: go.Set<go.Link>
): D3LayoutNode => {
  const link = links
    .filter((link) => link.data.labelKeys.includes(node.key))
    .first();
  return {
    key: toLayoutKey(node.diagram?.model?.getKeyForNodeData(node.data)),
    radius: NODE_SIZE / 2,
    // TODO find a way to properly position label nodes
    x: node.position.x,
    y: node.position.y,
    fx: node.position.x,
    fy: node.position.y,
    isLabel: true,
    labelForLink: toLayoutKey(link?.key),
  };
};

const mapNodesToLayoutNodes = (
  nodes: go.Set<go.Node>,
  links: go.Set<go.Link>
): D3LayoutNode[] =>
  nodes
    .map<D3LayoutNode>((node) =>
      !node.data.isLinkLabel
        ? mapCommonNodeToLayoutNode(node)
        : mapLinkLabelToLayoutNode(node, links)
    )
    .toArray();

const mapLinkToLayoutLink = (
  layoutSettings: ForceLayoutSettings,
  link: go.Link,
  linkLabel: go.TextBlock | null
) => ({
  source: toLayoutKey(link.fromNode?.key),
  target: toLayoutKey(link.toNode?.key),
  key: toLayoutKey(
    (link.diagram?.model as go.GraphLinksModel | undefined)?.getKeyForLinkData(
      link.data
    )
  ),
  textLength: getTextLength(layoutSettings, linkLabel),
  fromPerimeter: getWidthOfAllChildren(link.fromNode),
  toPerimeter: getWidthOfAllChildren(link.toNode),
  isArtificial: false,
  isEdgeProperty: false,
});

const mapEdgePropertyLinkToLayoutLinks = (
  layoutSettings: ForceLayoutSettings,
  link: go.Link,
  linkLabel: go.TextBlock | null,
  links: go.Set<go.Link>
) => {
  const labelKey = link.fromNode?.key;
  if (!labelKey) {
    return [];
  }
  const originalLink = links
    .filter((x) => x.data.labelKeys.includes(labelKey))
    .first();
  if (!originalLink) {
    return [];
  }
  return [
    {
      source: toLayoutKey(originalLink.fromNode?.key),
      target: toLayoutKey(link.toNode?.key),
      key: toLayoutKey(
        EDGE_PROPERTY_LEFT_LINK_PREFIX +
          (
            link.diagram?.model as go.GraphLinksModel | undefined
          )?.getKeyForLinkData(link.data)
      ),
      textLength: getTextLength(layoutSettings, linkLabel),
      fromPerimeter: getWidthOfAllChildren(link.fromNode),
      toPerimeter: getWidthOfAllChildren(link.toNode),
      isArtificial: false,
      isEdgeProperty: true,
    },
    {
      source: toLayoutKey(originalLink.toNode?.key),
      target: toLayoutKey(link.toNode?.key),
      key: toLayoutKey(
        EDGE_PROPERTY_RIGHT_LINK_PREFIX +
          (
            link.diagram?.model as go.GraphLinksModel | undefined
          )?.getKeyForLinkData(link.data)
      ),
      textLength: getTextLength(layoutSettings, linkLabel),
      fromPerimeter: getWidthOfAllChildren(link.fromNode),
      toPerimeter: getWidthOfAllChildren(link.toNode),
      isArtificial: false,
      isEdgeProperty: true,
    },
  ];
};

const mapLinksToLayoutLinks = (
  layoutSettings: ForceLayoutSettings,
  links: go.Set<go.Link>,
  getLinkLabelForLink: (link: go.Link) => go.TextBlock | null
): D3LayoutLink[] =>
  links
    .map<D3LayoutLink[]>((link) =>
      !link.data.isEdgeProperty
        ? [mapLinkToLayoutLink(layoutSettings, link, getLinkLabelForLink(link))]
        : mapEdgePropertyLinkToLayoutLinks(
            layoutSettings,
            link,
            getLinkLabelForLink(link),
            links
          )
    )
    .toArray()
    .flat();

const mapArtificialLinksToLayoutLinks = (
  links: ArtificialLink[]
): D3LayoutLink[] =>
  links.map<D3LayoutLink>((link) => ({
    source: toLayoutKey(link.from.key),
    target: toLayoutKey(link.to.key),
    key: toLayoutKey(
      `${ARTIFICIAL_LINK_PREFIX}-${link.from.key}:${link.to.key}`
    ),
    textLength: 0,
    fromPerimeter: getWidthOfAllChildren(link.from),
    toPerimeter: getWidthOfAllChildren(link.to),
    isArtificial: true,
    isEdgeProperty: false,
  }));
